/**
 * acctProfileContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";
//import { /*useParams, useHistory,*/ RouteChildrenProps } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import { Button, Checkbox, FormControl, FormLabel, IconButton, 
  InputAdornment, List, ListItem, Tooltip, TextField } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

import { SCAvatar } from "../components/avatar.component";
import { EditAvatarModal } from "./editAvatarModal.component";
import { TimeZoneSelect } from "./timeZoneSelect.component";

import { Membership, User } from "../models/auth";

import { authServiceFactory } from "../services/auth.service";

export function AccountProfileContainer(/*props: RouteChildrenProps*/): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);

  React.useEffect(function () {
    groupNavContext.setCurrentGroup(null);
  }, []);

  return <div className="both-side-margin main-section-container main-scroll-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
        disableRipple disableTouchRipple
        onClick={groupNavContext.toggleGroupList}>
        <MenuIcon />
      </IconButton>
      <h3>Profile for: {authContext.membership?.accountname || ""}</h3>
      <span className="right-controls">
      </span>
    </header>
    <div className="main-section-inner-with-header account-profile-container">
      <AccountProfileView />
    </div>
  </div>;
}

interface ProfileErrors {
  firstname: string,
  lastname: string
}
function AccountProfileView(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const [firstname, setFirstname] = React.useState<string>(authContext.membership?.firstname || "");
  const [lastname, setLastname] = React.useState<string>(authContext.membership?.lastname || "");
  const [sendOnReturn, setSendOnReturn] = React.useState<boolean>(authContext.membership?.sendOnReturn || false);
  const [tz, setTimeZone] = React.useState<string>("");
  const [errors, setErrors] = React.useState<ProfileErrors | null>(null);
  const [myUser, setMyUser] = React.useState<User | null>(null);
  const [fnameTimer, setFnameTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [lnameTimer, setLnameTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [openAvatarModel, setOpenAvatarModel] = React.useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = React.useState<string>("");
  const lnameRef = React.useRef<HTMLInputElement>(null);
  const membership = authContext.membership;

  React.useEffect(function () {
    if (!errors) {
      setErrors({
        firstname: "",
        lastname: ""
      });
    }
    const authService = authServiceFactory();

    if (membership) {
      setFirstname(membership.firstname);
      setLastname(membership.lastname);
      setSendOnReturn(membership.sendOnReturn || false);
      setSelectedEmail(membership.email || "");
      setTimeZone(membership.timezone || "");
      if (!membership.avatarText) {
        membership.avatarText = authService.makeAvatarText(membership);
      }
    }


    authService.getMe().then(setMyUser, err => {
      console.error("Error getting my user:", err);
    });
  }, [authContext.membership]);

  function updateSendOnReturn(event: React.ChangeEvent<HTMLInputElement>) {
    const sendOnReturn = event.target.checked;
    if (membership) {
      membership.sendOnReturn = sendOnReturn
    }
    setSendOnReturn(sendOnReturn);
    updateMembership({ sendOnReturn: sendOnReturn });
  }

  function updateFirstname(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const newValue = inputElem.value;

    setFirstname(newValue);
    if (!newValue) {
      return;
    }
    if (fnameTimer) { clearTimeout(fnameTimer); }
    setFnameTimer(setTimeout(() => {
      updateMembership({ firstname: newValue });
    }, 2000));
  }
  function revertFirstName() {
    if (!membership || !myUser) { return; }
    membership.firstname = myUser.firstname;
    setFirstname(myUser.firstname);

    updateMembership({ firstname: myUser.firstname });
  }

  function updateLastname(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const newValue = inputElem.value;

    setLastname(newValue);
    if (lnameTimer) { clearTimeout(lnameTimer); }
    setLnameTimer(setTimeout(() => {
      updateMembership({ lastname: newValue });
    }, 2000));
  }

  function revertLastName() {
    if (!membership || !myUser) { return; }
    membership.lastname = myUser.lastname;
    setLastname(myUser.lastname);
    updateMembership({ lastname: myUser.lastname });
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem);
      return;
    }
    let msg = "";
    switch (propName) {
      case "firstname":
        if (!firstname) {
          msg = "Enter a first name."
        }
        break;
      case "lastname":
        if (!lastname) {
          msg = "Enter a last name."
        }
        break;
    }
    if (errors) {
      setErrors({ ...errors, [propName]: msg });
    } else {
      setErrors({ firstname: "", lastname: "", [propName]: msg });
    }
  }

  function onKeyUp(event: React.KeyboardEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (event.key === "Enter") {
      switch (propName) {
        case "firstname":
          lnameRef.current?.focus();
          break;
      }
    }
  }

  function updateTimeZone(zone: string) {
    setTimeZone(zone);
    if (membership) {
      membership.timezone = zone;
    }
    updateMembership({ timezone: zone });
  }

  async function updateMembership(update: Partial<Membership>) {
    try {
      if (!membership) { return; }
      await authServiceFactory().updateMembership(membership.userID, update);
    } catch (err) {
      console.error("Error updating membership:", err);
    }
  }
  /**
   * Update the email to be displayed 
   * @param email to update as being selected
   */
  function updateSelectedEmail(email: string) {
    //should never happen but guard
    if (!email) {
      console.log("No selected email");
      return;
    }
    setSelectedEmail(email);
    updateMembership({ email });
  }



  function finishedAvatar() {
    setOpenAvatarModel(false);
  }

  if (!membership) { return <></>; }

  /*<div className="form-row">
          <FormLabel component="legend">Display Name</FormLabel>
        </div>*/
  return <div className="account-profile-form-container">
    <div className="avatar-edit-container">
      <SCAvatar text={membership.avatarText || "XX"} url={membership.avatarUrl} />
      <Button disableRipple disableTouchRipple
        onClick={() => setOpenAvatarModel(true)}>Edit</Button>
    </div>
    <form className="form-wrapper">
      <section>
        <div className="form-row">
          <FormControl fullWidth>
            <TextField id="firstname-input" label="First Name" name="firstname"
              type="text" size="small"
              error={!!errors?.firstname}
              helperText={errors?.firstname}
              variant="outlined"
              value={firstname}
              onChange={updateFirstname}
              onBlur={onBlur}
              onKeyUp={onKeyUp}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <IconButton onClick={revertFirstName} aria-label="Revert First Name" size="small">
                      <Tooltip title="Revert to Settings value">
                        <i className="fa-solid fa-rotate-left" />
                      </Tooltip>
                    </IconButton>
                  </InputAdornment>
              }} />
          </FormControl>
        </div>
        <div className="form-row">
          <FormControl fullWidth>
            <TextField id="lastname-input" label="Last Name" name="lastname"
              type="text" size="small"
              error={!!errors?.lastname}
              helperText={errors?.lastname}
              variant="outlined"
              value={lastname}
              inputRef={lnameRef}
              onChange={updateLastname}
              onBlur={onBlur}
              onKeyUp={onKeyUp}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <IconButton onClick={revertLastName} aria-label="Revert Last Name" size="small">
                      <Tooltip title="Revert to Settings value">
                        <i className="fa-solid fa-rotate-left" />
                      </Tooltip>
                    </IconButton>
                  </InputAdornment>
              }} />
          </FormControl>
        </div>
        <div className="form-row">
          <FormLabel>
            <Checkbox checked={sendOnReturn} onChange={updateSendOnReturn} />
            <div className="radio-label-desc">
              <dt>Send on Enter Key</dt>
              <dd>When enabled, the Enter/Return will cause the message editor to send
                the current message. Shift+Enter will insert a new line into the message.
                When disabled, Tab followed by Enter will send the current message.
              </dd>
            </div>
          </FormLabel>
        </div>
        <div className="form-row">
          <TimeZoneSelect zone={tz} didSelectZone={updateTimeZone} />
        </div>
        <div className="form-row">
          <h2>Select Displayed Email </h2>
          <List>
            {
              myUser?.emails.map(email => (
                <ListItem key={email}>
                  <Checkbox aria-label={email}
                    checked={email === selectedEmail}
                    onClick={() => updateSelectedEmail(email)} />
                  {email}
                </ListItem>
              ))
            }
          </List>
        </div>

      </section>
    </form>
    <EditAvatarModal open={openAvatarModel}
      didChangeAvatar={finishedAvatar}
      onClose={() => setOpenAvatarModel(false)} />
  </div>;
}

